import { useEffect, useId, useRef, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';

const cx = (...c: Array<string | false | null | undefined>) => c.filter(Boolean).join(' ');

export function Button({ tone = 'primary', busy, className, children, ...rest }: ButtonHTMLAttributes<HTMLButtonElement> & { tone?: 'primary' | 'ghost' | 'danger' | 'quiet'; busy?: boolean }) {
  return (
    <button
      {...rest}
      disabled={rest.disabled || busy}
      className={cx(
        'inline-flex min-h-11 items-center justify-center gap-2 px-5 font-mono text-[12px] tracking-[0.16em] uppercase transition-colors disabled:cursor-not-allowed disabled:opacity-50',
        tone === 'primary' && 'bg-gold text-bg hover:bg-gold-strong',
        tone === 'ghost' && 'border border-line text-ink hover:border-gold',
        tone === 'danger' && 'border border-bad/60 text-bad hover:bg-bad/10',
        tone === 'quiet' && 'px-2 text-ash normal-case tracking-normal font-sans text-sm hover:text-ink',
        className,
      )}
    >
      {busy ? <Spinner small /> : null}
      {children}
    </button>
  );
}

export function Spinner({ small }: { small?: boolean }) {
  return <span role="status" aria-label="Loading" className={cx('inline-block animate-spin rounded-full border-2 border-current border-t-transparent', small ? 'h-3.5 w-3.5' : 'h-6 w-6 text-gold')} />;
}

export function Loading() {
  return (
    <div className="flex min-h-40 items-center justify-center">
      <Spinner />
    </div>
  );
}

export function ErrorNote({ error }: { error: unknown }) {
  if (!error) return null;
  return (
    <p role="alert" className="border border-bad/50 bg-bad/10 px-4 py-3 text-sm text-[#f5c2c2]">
      {error instanceof Error ? error.message : String(error)}
    </p>
  );
}

export function Notice({ children, tone = 'info' }: { children: ReactNode; tone?: 'info' | 'ok' | 'warn' }) {
  return (
    <div className={cx('border px-4 py-3 text-sm', tone === 'info' && 'border-gold/50 bg-gold/5 text-ink', tone === 'ok' && 'border-ok/50 bg-ok/10 text-ink', tone === 'warn' && 'border-warn/60 bg-warn/10 text-ink')}>{children}</div>
  );
}

interface FieldShell {
  label: string;
  hint?: ReactNode;
  required?: boolean;
  error?: string;
}

function Label({ id, label, required }: { id: string; label: string; required?: boolean }) {
  return (
    <label htmlFor={id} className="block text-sm font-medium text-ink">
      {label}
      {required && <span className="ml-1 text-gold">*</span>}
    </label>
  );
}

const inputCls = 'min-h-11 w-full border border-line bg-bg px-3.5 py-2.5 text-base text-ink placeholder:text-dim focus:border-gold';

export function Field({ label, hint, required, error, className, ...rest }: FieldShell & InputHTMLAttributes<HTMLInputElement>) {
  const id = useId();
  return (
    <div className={cx('space-y-1.5', className)}>
      <Label id={id} label={label} required={required} />
      <input id={id} aria-describedby={hint ? `${id}-h` : undefined} aria-invalid={Boolean(error)} className={inputCls} {...rest} />
      {hint && <p id={`${id}-h`} className="text-sm text-dim">{hint}</p>}
      {error && <p className="text-sm text-bad">{error}</p>}
    </div>
  );
}

export function TextArea({ label, hint, required, className, ...rest }: FieldShell & TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const id = useId();
  return (
    <div className={cx('space-y-1.5', className)}>
      <Label id={id} label={label} required={required} />
      <textarea id={id} rows={4} aria-describedby={hint ? `${id}-h` : undefined} className={inputCls} {...rest} />
      {hint && <p id={`${id}-h`} className="text-sm text-dim">{hint}</p>}
    </div>
  );
}

export function Select({ label, hint, required, options, className, ...rest }: FieldShell & SelectHTMLAttributes<HTMLSelectElement> & { options: ReadonlyArray<readonly [string, string]> }) {
  const id = useId();
  return (
    <div className={cx('space-y-1.5', className)}>
      <Label id={id} label={label} required={required} />
      <select id={id} className={inputCls} {...rest}>
        {options.map(([v, l]) => (
          <option key={v} value={v}>
            {l}
          </option>
        ))}
      </select>
      {hint && <p className="text-sm text-dim">{hint}</p>}
    </div>
  );
}

export function Check({ label, checked, onChange, hint }: { label: ReactNode; checked: boolean; onChange: (v: boolean) => void; hint?: string }) {
  const id = useId();
  return (
    <label htmlFor={id} className="flex min-h-11 cursor-pointer items-start gap-3 text-sm text-ink">
      <input id={id} type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="mt-1 h-4 w-4 shrink-0 accent-[#d6be8e]" />
      <span>
        {label}
        {hint && <span className="block text-dim">{hint}</span>}
      </span>
    </label>
  );
}

export function Card({ title, action, children, className }: { title?: ReactNode; action?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={cx('border border-line bg-card p-5', className)}>
      {(title || action) && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          {title && <h2 className="text-lg font-medium">{title}</h2>}
          {action}
        </div>
      )}
      {children}
    </section>
  );
}

export function PageHeader({ eyebrow, title, sub, actions }: { eyebrow?: string; title: string; sub?: ReactNode; actions?: ReactNode }) {
  return (
    <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
      <div className="min-w-0">
        {eyebrow && <p className="eyebrow mb-2">{eyebrow}</p>}
        <h1 className="text-3xl font-light tracking-tight text-balance md:text-4xl">{title}</h1>
        {sub && <p className="mt-2 max-w-2xl text-ash">{sub}</p>}
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </header>
  );
}

const TONES = {
  neutral: 'border-line text-ash',
  gold: 'border-gold/60 text-gold',
  ok: 'border-ok/60 text-ok',
  warn: 'border-warn/60 text-warn',
  bad: 'border-bad/60 text-bad',
} as const;

export function Badge({ children, tone = 'neutral' }: { children: ReactNode; tone?: keyof typeof TONES }) {
  return <span className={cx('inline-flex items-center border px-2 py-0.5 font-mono text-[11px] tracking-wide whitespace-nowrap uppercase', TONES[tone])}>{children}</span>;
}

export function stageTone(stage: string): keyof typeof TONES {
  return ({ APPLIED: 'gold', ACTIVE: 'ok', ONBOARDING: 'gold', APPROVED: 'ok', INACTIVE: 'warn', SUSPENDED: 'bad', ENDED: 'neutral', DECLINED: 'neutral' } as Record<string, keyof typeof TONES>)[stage] ?? 'neutral';
}

export function Stat({ label, value, hint, tone }: { label: string; value: ReactNode; hint?: string; tone?: 'warn' | 'bad' }) {
  return (
    <div className="border border-line bg-card p-4">
      <p className="eyebrow">{label}</p>
      <p className={cx('mt-2 text-3xl font-light tabular-nums', tone === 'warn' && 'text-warn', tone === 'bad' && 'text-bad')}>{value}</p>
      {hint && <p className="mt-1 text-sm text-dim">{hint}</p>}
    </div>
  );
}

export function Empty({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="border border-dashed border-line px-6 py-10 text-center">
      <p className="text-ink">{title}</p>
      {children && <div className="mt-2 text-sm text-ash">{children}</div>}
    </div>
  );
}

export interface Column<T> {
  key: string;
  label: string;
  render: (row: T) => ReactNode;
  className?: string;
}

/** A table on wide screens; stacked cards on phones. */
export function Table<T>({ rows, columns, onRow, empty }: { rows: ReadonlyArray<T>; columns: ReadonlyArray<Column<T>>; onRow?: (row: T) => void; empty?: string }) {
  if (!rows.length) return <Empty title={empty ?? 'Nothing here yet.'} />;
  return (
    <>
      <div className="hidden overflow-x-auto md:block">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr>
              {columns.map((c) => (
                <th key={c.key} className="border-b border-line px-3 py-2 text-left font-mono text-[11px] font-normal tracking-[0.14em] text-dim uppercase">
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} onClick={onRow ? () => onRow(r) : undefined} className={cx('border-b border-line/60', onRow && 'cursor-pointer hover:bg-raised')}>
                {columns.map((c) => (
                  <td key={c.key} className={cx('px-3 py-3 align-top', c.className)}>
                    {c.render(r)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ul className="space-y-2 md:hidden">
        {rows.map((r, i) => (
          <li key={i}>
            <button type="button" disabled={!onRow} onClick={onRow ? () => onRow(r) : undefined} className="w-full border border-line bg-card p-4 text-left">
              <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
                {columns.map((c) => (
                  <div key={c.key} className="contents">
                    <dt className="text-dim">{c.label}</dt>
                    <dd className="min-w-0 break-words">{c.render(r)}</dd>
                  </div>
                ))}
              </dl>
            </button>
          </li>
        ))}
      </ul>
    </>
  );
}

export function Tabs({ tabs, value, onChange }: { tabs: ReadonlyArray<readonly [string, string]>; value: string; onChange: (v: string) => void }) {
  return (
    <div role="tablist" className="mb-5 flex gap-1 overflow-x-auto border-b border-line">
      {tabs.map(([v, l]) => (
        <button key={v} role="tab" type="button" aria-selected={value === v} onClick={() => onChange(v)} className={cx('min-h-11 shrink-0 border-b-2 px-3 text-sm', value === v ? 'border-gold text-ink' : 'border-transparent text-ash hover:text-ink')}>
          {l}
        </button>
      ))}
    </div>
  );
}

export function Modal({ open, onClose, title, children }: { open: boolean; onClose: () => void; title: string; children: ReactNode }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);
  return (
    <dialog ref={ref} onClose={onClose} className="m-auto w-[min(640px,calc(100vw-32px))] border border-line bg-card p-0 text-ink backdrop:bg-black/70">
      <div className="flex items-center justify-between border-b border-line px-5 py-3">
        <h2 className="text-lg font-medium">{title}</h2>
        <button type="button" onClick={onClose} aria-label="Close" className="min-h-11 min-w-11 text-ash hover:text-ink">
          ✕
        </button>
      </div>
      <div className="max-h-[75vh] overflow-y-auto p-5">{open ? children : null}</div>
    </dialog>
  );
}

export function KV({ items }: { items: ReadonlyArray<readonly [string, ReactNode]> }) {
  return (
    <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-[minmax(140px,auto)_1fr]">
      {items
        .filter(([, v]) => v !== null && v !== undefined && v !== '')
        .map(([k, v]) => (
          <div key={k} className="contents">
            <dt className="text-dim">{k}</dt>
            <dd className="min-w-0 break-words">{v}</dd>
          </div>
        ))}
    </dl>
  );
}
